import { useEffect, useMemo, useRef, useState } from 'react';
import { scrollPageTo } from '@/lib/scroll';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Check, CheckCircle2, ChevronLeft, ChevronRight, Info, Palette, Pause, Play, Plus, Sparkles, Trash2 } from 'lucide-react';
import { isTajweed } from '@/features/quran/fonts';
import { addDays, dayKeyIn } from '@shared/prayer';
import { alive, useProgress, useSettings } from '@/lib/store';
import { Card, IconButton, LargeTitle, Ring, Row, Section, Segmented, Sheet, Stepper, haptic } from '@/components/ui';
import { CHAPTERS, chapter, cleanTranslation, parseKey, versesByChapter, type Verse } from '@/features/quran/api';
import { TajweedText } from '@/features/quran/Tajweed';
import { Ornament, StarIcon } from '@/components/islamic/Ornaments';
import { usePlayer } from '@/features/player/audioEngine';
import { useReadingTimer } from '@/features/progress/useReadingTimer';
import { alignTajweed } from '@/features/quran/tajweedAlign';
import { BASMALAH_INDOPAK, BASMALAH_QPC, indopakVerse, qpcVerse } from '@/features/quran/script';
import { PRESETS, cycleEnd, cycleLabel, khatmahProgress, letterCount, scopeKeys, scopeLabel, sumDays, type Khatmah, type KhatmahCycle, type KhatmahScope } from './khatmah';

const fmtNum = (n: number) => n.toLocaleString('en-GB');
const useToday = () => dayKeyIn(useSettings((s) => s.settings.prayer.tz));

function useRolledOver() {
  const today = useToday();
  const roll = useProgress((s) => s.rolloverKhatmahs);
  useEffect(() => { roll(today); }, [roll, today]);
  return today;
}

/* ── List ───────────────────────────────────────────────────────────── */
export function KhatmahList() {
  const today = useRolledOver();
  const plans = alive(useProgress((s) => s.khatmahs)).sort((a, b) => a.createdAt - b.createdAt);
  const log = useProgress((s) => s.ayahLog);
  const [creating, setCreating] = useState(false);
  const todayStats = log[today];
  const week = sumDays(Array.from({ length: 7 }, (_, i) => log[addDays(today, -i)]));
  const all = sumDays(Object.values(log));

  return (
    <div className="page-pad">
      <LargeTitle back="/" title="Khatmah" ar="الختمة" subtitle="Your reading plans" right={<IconButton label="New plan" onClick={() => setCreating(true)}><Plus size={20} /></IconButton>} />

      <div className="grid grid-cols-3 gap-3 px-4 pb-5">
        {([['Today', sumDays([todayStats])], ['7 days', week], ['All time', all]] as const).map(([label, { ayahs: a, hasanat: h, pages: pg }]) => (
          <Card key={label as string} className="p-3 text-center">
            <div className="text-[12px] font-semibold uppercase tracking-wide text-muted">{label}</div>
            <div className="mt-1 text-[22px] font-bold tabular-nums leading-tight">{fmtNum(a)}</div>
            <div className="text-[11px] text-muted">āyāt · {fmtNum(pg)} {pg === 1 ? 'page' : 'pages'}</div>
            <div className="mt-1 text-[13px] font-semibold tabular-nums text-gold">{fmtNum(h)}</div>
            <div className="text-[11px] text-muted">ḥasanāt*</div>
          </Card>
        ))}
      </div>

      {plans.length === 0 ? (
        <div className="px-4 pb-6">
          <Card className="p-5 text-center">
            <StarIcon size={40} className="mx-auto text-gold" />
            <div className="mt-2 text-[18px] font-bold">Start a khatmah</div>
            <p className="mt-1 text-[14px] text-muted">Run as many plans as you like side by side — the whole Qurʾan, Al-Baqarah every two weeks, Yā-Sīn every day.</p>
            <button type="button" onClick={() => setCreating(true)} className="press mt-4 rounded-full bg-accent px-5 py-2.5 font-semibold text-accent-ink">Create a plan</button>
          </Card>
        </div>
      ) : (
        <div className="space-y-3 px-4 pb-6">
          {plans.map((k) => <PlanCard key={k.id} k={k} today={today} />)}
          <button type="button" onClick={() => setCreating(true)} className="press flex w-full items-center justify-center gap-2 rounded-[20px] border border-dashed border-hairline py-4 font-semibold text-muted"><Plus size={18} /> Add another plan</button>
        </div>
      )}
      <p className="px-8 pb-6 text-[12px] leading-snug text-muted">* “Whoever reads a letter from the Book of Allah receives a good deed, and a good deed is multiplied by ten” (at-Tirmidhī 2910). Counted from the letters of each āyah you read — an estimate; Allah multiplies for whom He wills.</p>
      <NewPlanSheet open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function PlanCard({ k, today }: { k: Khatmah; today: string }) {
  const p = khatmahProgress(k, today);
  const del = useProgress((s) => s.deleteKhatmah);
  const next = p.nextKey ? parseKey(p.nextKey) : null;
  const end = cycleEnd(k);
  const todayPct = p.dailyTarget ? Math.min(1, p.todayDone / p.dailyTarget) : null;
  return (
    <Card className={`p-4 ${p.doneToday ? 'ring-2 ring-[var(--win-ok)]/50' : ''}`}>
      <div className="flex items-center gap-4">
        <Ring value={p.pct} size={64} stroke={7}><span className="text-[13px] font-bold tabular-nums">{Math.round(p.pct * 100)}%</span></Ring>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div className="truncate text-[17px] font-bold">{k.title}</div>
            {p.doneToday && <CheckCircle2 size={18} className="shrink-0 text-[var(--win-ok)]" aria-label="Done for today" />}
          </div>
          <div className="truncate text-[13px] text-muted">{scopeLabel(k.scope)} · {cycleLabel(k.cycle)}{k.completions ? ` · completed ${k.completions}×` : ''}</div>
          <div className="mt-0.5 text-[13px] tabular-nums">{fmtNum(p.done)} / {fmtNum(p.total)} āyāt{end ? ` · by ${new Date(`${end}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })}` : ''}</div>
        </div>
        <button type="button" aria-label={`Delete ${k.title}`} onClick={() => { if (confirm(`Delete "${k.title}"?`)) del(k.id); }} className="press p-2 text-faint"><Trash2 size={16} /></button>
      </div>
      {todayPct != null && (
        <div className="mt-3">
          <div className="flex justify-between text-[12px] text-muted"><span>Today {p.todayDone}/{p.dailyTarget} āyāt</span>{p.doneToday ? <span className="flex items-center gap-1 font-semibold text-[var(--win-ok)]"><Check size={13} /> Done for today</span> : p.catchUp > 0 ? <span className="text-[var(--win-warn)]">incl. {p.catchUp} to catch up</span> : <span className="text-[var(--win-ok)]">On track</span>}</div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-fill"><div className={`h-full rounded-full transition-all ${p.doneToday ? 'bg-[var(--win-ok)]' : 'bg-accent'}`} style={{ width: `${todayPct * 100}%` }} /></div>
        </div>
      )}
      <Link to={`/khatmah/${k.id}`} className="press mt-3 flex items-center justify-between rounded-2xl bg-accent px-4 py-3 font-semibold text-accent-ink">
        <span>{p.done === 0 ? 'Begin' : 'Continue'}{next ? ` · ${chapter(next.surah).n} ${next.ayah}` : ''}</span>
        <ChevronRight size={18} />
      </Link>
    </Card>
  );
}

function NewPlanSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const save = useProgress((s) => s.saveKhatmah);
  const nav = useNavigate();
  const [kind, setKind] = useState<KhatmahScope['kind']>('quran');
  const [surah, setSurah] = useState(36);
  const [juz, setJuz] = useState(30);
  const [from, setFrom] = useState({ s: 1, a: 1 });
  const [to, setTo] = useState({ s: 2, a: 286 });
  const [repeat, setRepeat] = useState<'none' | 'repeat'>('repeat');
  const [cycle, setCycle] = useState<KhatmahCycle>({ every: 1, unit: 'month' });
  const [title, setTitle] = useState('');

  const scope: KhatmahScope = kind === 'quran' ? { kind } : kind === 'surah' ? { kind, surah } : kind === 'juz' ? { kind, juz } : { kind, from: `${from.s}:${from.a}`, to: `${to.s}:${to.a}` };
  const total = scopeKeys(scope).length;
  const days = repeat === 'none' ? null : cycle.unit === 'day' ? cycle.every : cycle.unit === 'week' ? cycle.every * 7 : cycle.every * 30;
  const autoTitle = `${scopeLabel(scope).replace('Sūrat ', '')} ${repeat === 'none' ? '— at my own pace' : `— ${cycleLabel(cycle).toLowerCase()}`}`;

  const create = (t: string, sc: KhatmahScope, cy: KhatmahCycle | null) => {
    const id = save({ title: t, scope: sc, cycle: cy });
    onClose();
    nav(`/khatmah/${id}`);
  };
  const select = 'rounded-lg bg-fill px-2 py-1.5 text-[15px] text-ink max-w-[60%]';
  return (
    <Sheet open={open} onClose={onClose} title="New khatmah plan">
      <Section title="Quick start">
        {PRESETS.map((p) => (
          <Row key={p.title} label={<span><span className="block font-medium">{p.title}</span><span className="block text-[12px] text-muted">{scopeLabel(p.scope)} · {cycleLabel(p.cycle)} · {fmtNum(scopeKeys(p.scope).length)} āyāt</span></span>} onClick={() => create(p.title, p.scope, p.cycle)} />
        ))}
      </Section>
      <Section title="Custom">
        <div className="row"><Segmented label="What to read" value={kind} onChange={setKind} options={[{ value: 'quran', label: "Qurʾan" }, { value: 'surah', label: 'Surah' }, { value: 'juz', label: 'Juz' }, { value: 'range', label: 'Range' }]} /></div>
        {kind === 'surah' && (
          <Row label="Surah"><select aria-label="Surah" className={select} value={surah} onChange={(e) => setSurah(Number(e.target.value))}>{CHAPTERS.map((c) => <option key={c.id} value={c.id}>{c.id}. {c.n}</option>)}</select></Row>
        )}
        {kind === 'juz' && <Row label="Juz"><Stepper value={juz} min={1} max={30} onChange={setJuz} /></Row>}
        {kind === 'range' && (
          <>
            <Row label="From"><span className="flex gap-1"><select aria-label="From surah" className={select} value={from.s} onChange={(e) => setFrom({ s: Number(e.target.value), a: 1 })}>{CHAPTERS.map((c) => <option key={c.id} value={c.id}>{c.id}. {c.n}</option>)}</select><input aria-label="From āyah" inputMode="numeric" className="w-14 rounded-lg bg-fill px-2 text-center" value={from.a} onChange={(e) => setFrom({ ...from, a: Math.max(1, Math.min(CHAPTERS[from.s - 1].v, Number(e.target.value.replace(/\D/g, '')) || 1)) })} /></span></Row>
            <Row label="To"><span className="flex gap-1"><select aria-label="To surah" className={select} value={to.s} onChange={(e) => { const s = Number(e.target.value); setTo({ s, a: CHAPTERS[s - 1].v }); }}>{CHAPTERS.map((c) => <option key={c.id} value={c.id} disabled={c.id < from.s}>{c.id}. {c.n}</option>)}</select><input aria-label="To āyah" inputMode="numeric" className="w-14 rounded-lg bg-fill px-2 text-center" value={to.a} onChange={(e) => setTo({ ...to, a: Math.max(1, Math.min(CHAPTERS[to.s - 1].v, Number(e.target.value.replace(/\D/g, '')) || 1)) })} /></span></Row>
          </>
        )}
        <div className="row"><Segmented label="Rhythm" value={repeat} onChange={setRepeat} options={[{ value: 'repeat', label: 'Repeating' }, { value: 'none', label: 'At my own pace' }]} /></div>
        {repeat === 'repeat' && (
          <Row label="Finish it every">
            <span className="flex items-center gap-2">
              <Stepper value={cycle.every} min={1} max={60} onChange={(v) => setCycle({ ...cycle, every: v })} />
              <select aria-label="Unit" className="rounded-lg bg-fill px-2 py-1.5 text-ink" value={cycle.unit} onChange={(e) => setCycle({ ...cycle, unit: e.target.value as KhatmahCycle['unit'] })}>
                <option value="day">day{cycle.every > 1 ? 's' : ''}</option><option value="week">week{cycle.every > 1 ? 's' : ''}</option><option value="month">month{cycle.every > 1 ? 's' : ''}</option>
              </select>
            </span>
          </Row>
        )}
        <Row label="Name"><input aria-label="Plan name" placeholder={autoTitle} value={title} onChange={(e) => setTitle(e.target.value)} className="w-[60%] rounded-lg bg-fill px-2 py-1.5 text-[15px] text-ink" /></Row>
      </Section>
      <p className="px-8 pb-3 text-[13px] text-muted">{fmtNum(total)} āyāt{days ? ` · about ${fmtNum(Math.ceil(total / days))} a day` : ''}</p>
      <div className="px-4"><button type="button" disabled={!total} onClick={() => create(title || autoTitle, scope, repeat === 'none' ? null : cycle)} className="press w-full rounded-2xl bg-accent py-3.5 text-[17px] font-semibold text-accent-ink disabled:opacity-40">Start plan</button></div>
    </Sheet>
  );
}

/* ── Focus reader: one āyah at a time ───────────────────────────────── */

/** Whether `v` is the last āyah on its mushaf page: the next āyah in mushaf
    order (same surah, or the first page of the next surah) is on another page. */
function endsPage(v: Verse, surahVerses: Verse[] | undefined) {
  const s = parseKey(v.verse_key).surah;
  const after = surahVerses?.find((x) => x.verse_number === v.verse_number + 1);
  if (after) return after.page_number !== v.page_number;
  if (v.verse_number < chapter(s).v) return false; // not loaded yet — never over-count
  return s === 114 || chapter(s + 1).p[0] !== v.page_number;
}
export function FocusReader() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const today = useRolledOver();
  const k = useProgress((s) => s.khatmahs[id]);
  const readAyah = useProgress((s) => s.readAyah);
  const stepBack = useProgress((s) => s.stepBack);
  const todayLog = useProgress((s) => s.ayahLog[today]);
  const { translationId, showTranslation, mushaf, arabicSize } = useSettings((s) => s.settings);
  const update = useSettings((s) => s.update);
  const qc = useQueryClient();
  const [celebrate, setCelebrate] = useState<number | null>(null);
  const [lastGain, setLastGain] = useState<number | null>(null);
  const [info, setInfo] = useState(false);
  const player = usePlayer();

  const keys = useMemo(() => (k ? scopeKeys(k.scope) : []), [k]);
  const key = k ? keys[Math.min(k.position, keys.length - 1)] : null;
  const { surah, ayah } = key ? parseKey(key) : { surah: 1, ayah: 1 };
  const verses = useQuery({ queryKey: ['surah', surah, translationId], queryFn: () => versesByChapter(surah, translationId), staleTime: Infinity, enabled: !!key });
  const verse: Verse | undefined = verses.data?.find((v) => v.verse_key === key);
  useReadingTimer(verse ? [verse.page_number] : [], surah);

  // Prefetch the next surah as we approach its end.
  useEffect(() => {
    if (!k || !key) return;
    const nextKey = keys[k.position + 3];
    if (nextKey) {
      const ns = parseKey(nextKey).surah;
      if (ns !== surah) void qc.prefetchQuery({ queryKey: ['surah', ns, translationId], queryFn: () => versesByChapter(ns, translationId), staleTime: Infinity });
    }
  }, [k, key, keys, qc, surah, translationId]);

  const touch = useRef<number | null>(null);
  if (!k || k.deleted) {
    return <div className="page-pad px-6 text-center"><p className="pt-20 text-muted">This plan no longer exists.</p><Link to="/khatmah" className="font-semibold text-accent">Back to plans</Link></div>;
  }
  const p = khatmahProgress(k, today);

  const next = () => {
    if (!verse) return;
    const letters = letterCount(verse.text_uthmani);
    haptic(p.dailyTarget != null && p.todayDone + 1 === p.dailyTarget ? 80 : 10); // a stronger buzz when this āyah reaches today's target
    setLastGain(letters * 10);
    if (readAyah(k.id, today, letters, endsPage(verse, verses.data))) setCelebrate(k.completions + 1);
    scrollPageTo(0, true);
  };
  const back = () => {
    if (k.position === 0) return;
    // Letters of the āyah we're stepping back to — usually the same surah, otherwise from the query cache.
    const prevKey = keys[k.position - 1];
    const prevSurah = parseKey(prevKey).surah;
    const pool = prevSurah === surah ? verses.data : qc.getQueryData<Verse[]>(['surah', prevSurah, translationId]);
    const prev = pool?.find((v) => v.verse_key === prevKey);
    haptic();
    // The page tally only drops if the āyah being undone was the last on its page.
    stepBack(k.id, today, prev ? letterCount(prev.text_uthmani) : 0, !!prev && !!verse && prev.page_number !== verse.page_number);
    setLastGain(null);
  };
  const tajweed = isTajweed(mushaf);
  const indopakText = mushaf === 'indopak' && verse ? indopakVerse(verse) : null;
  const hearing = player.surah === surah && player.range?.from === ayah && (player.status === 'playing' || player.status === 'gap');
  const translation = verse?.translations?.[0]?.text;

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="glass sticky top-0 z-30 px-3 pb-2" style={{ paddingTop: 'calc(var(--safe-top) + 8px)' }}>
        <div className="flex items-center gap-2">
          <IconButton label="Back to plans" onClick={() => nav('/khatmah')}><ChevronLeft size={20} /></IconButton>
          <div className="min-w-0 flex-1 text-center">
            <div className="truncate text-[15px] font-bold">{k.title}</div>
            <div className="text-[12px] tabular-nums text-muted">{fmtNum(p.done)} / {fmtNum(p.total)} āyāt · {Math.round(p.pct * 100)}%</div>
          </div>
          <IconButton label={tajweed ? 'Turn tajweed colours off' : 'Turn tajweed colours on'} active={tajweed} onClick={() => update({ mushaf: tajweed ? 'uthmani' : 'tajweed-text' })}><Palette size={18} /></IconButton>
          <IconButton label="About ḥasanāt" onClick={() => setInfo(true)}><Info size={18} /></IconButton>
        </div>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-fill"><div className="h-full rounded-full bg-gradient-to-r from-accent to-gold transition-all duration-500" style={{ width: `${p.pct * 100}%` }} /></div>
      </header>

      {/* today's tally */}
      <div className="flex items-center justify-center gap-3 px-4 pt-4">
        <span className={`rounded-full px-3 py-1.5 text-[13px] font-semibold tabular-nums ${p.doneToday ? 'bg-[var(--win-ok)]/15 text-[var(--win-ok)]' : 'bg-fill'}`}>
          {p.doneToday && <Check size={13} className="mr-1 inline" aria-hidden />}{fmtNum(todayLog?.ayahs ?? 0)} āyāt today{p.dailyTarget ? ` · goal ${p.todayDone}/${p.dailyTarget}` : ''}
        </span>
        <span className="rounded-full bg-fill px-3 py-1.5 text-[13px] font-semibold tabular-nums" aria-label="Pages read today"><BookOpen size={13} className="mr-1 inline" />{fmtNum(todayLog?.pages ?? 0)} {todayLog?.pages === 1 ? 'page' : 'pages'}</span>
        <span className="relative rounded-full bg-gold/15 px-3 py-1.5 text-[13px] font-semibold tabular-nums text-gold">
          <Sparkles size={13} className="mr-1 inline" />{fmtNum(todayLog?.hasanat ?? 0)} ḥasanāt
          {lastGain != null && <span key={todayLog?.ayahs} className="absolute -top-5 right-2 animate-[fade_1.2s_ease_both] text-[12px]">+{fmtNum(lastGain)}</span>}
        </span>
      </div>

      <main className="mx-auto flex w-full max-w-[680px] flex-1 flex-col justify-center px-5 py-6"
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; touch.current = null; if (dx > 60) next(); else if (dx < -60) back(); }}>
        {verse ? (
          <article key={verse.verse_key} className="animate-[sheetUp_.35s_var(--ease)_both]">
            {ayah === 1 && (
              <div className="pb-5 text-center">
                <div className="font-kufi text-[22px] text-gold" dir="rtl">سورة {chapter(surah).ar}</div>
                <div className="text-[13px] text-muted">{chapter(surah).n} · {chapter(surah).t}</div>
                {surah !== 1 && surah !== 9 && (indopakText
                  ? <div className="ar-indopak pt-3 text-[24px]" dir="rtl">{BASMALAH_INDOPAK}</div>
                  : <div className="pt-3 text-[24px] [font-family:UthmanicHafs]" dir="rtl">{BASMALAH_QPC}</div>)}
                <Ornament className="pt-4" />
              </div>
            )}
            <button type="button" onClick={next} className="block w-full text-center" aria-label="Next āyah">
              {/* The King Fahd text for the Uthmani font, the Nastaleeq text for IndoPak (see quran/script.ts). */}
              <p className={`ayah-text !text-center ${indopakText ? 'indopak' : 'uthmani'}`} dir="rtl" style={{ fontSize: `${2.1 * arabicSize}rem`, lineHeight: 2.2 }}>
                {tajweed && verse.text_uthmani_tajweed ? <TajweedText html={alignTajweed(qpcVerse(verse), verse.text_uthmani_tajweed, false)} /> : indopakText ?? qpcVerse(verse)}
                <span className="ayah-num"> ﴿{ayah.toLocaleString('ar-EG')}﴾</span>
              </p>
            </button>
            {showTranslation && translation && <p className="mt-5 text-center text-[17px] leading-relaxed text-ink/90">{cleanTranslation(translation)}</p>}
            <div className="mt-4 text-center text-[12px] text-muted">{chapter(surah).n} {key} · {fmtNum(letterCount(verse.text_uthmani))} letters · {fmtNum(letterCount(verse.text_uthmani) * 10)} ḥasanāt</div>
          </article>
        ) : (
          <div className="text-center text-muted">{verses.error ? 'Couldn’t load this āyah — check your connection.' : 'Loading…'}</div>
        )}
      </main>

      <footer className="sticky bottom-0 px-4 pb-[calc(var(--safe-bottom)+12px)] pt-2">
        <div className="mx-auto flex max-w-[680px] items-center gap-2">
          {p.doneToday ? (
            /* Today's target is met: this is the "stop reading" button, where Back (one āyah) was. Swiping left still steps back. */
            <button type="button" onClick={() => { haptic(40); nav('/khatmah'); }} className="press flex h-14 items-center gap-1.5 rounded-2xl bg-[var(--win-ok)]/15 px-4 font-semibold text-[var(--win-ok)]"><CheckCircle2 size={20} /> Khatmah done</button>
          ) : (
            <button type="button" aria-label="Previous āyah" disabled={k.position === 0} onClick={back} className="press flex h-14 items-center gap-1 rounded-2xl bg-fill px-4 font-semibold disabled:opacity-30"><ChevronLeft size={20} /> Back</button>
          )}
          <button type="button" aria-label={hearing ? 'Stop' : 'Listen to this āyah'} onClick={() => (hearing ? player.pause() : void player.play(surah, { fromAyah: ayah, toAyah: ayah }))} className="press grid h-14 w-14 place-items-center rounded-2xl bg-fill">{hearing ? <Pause size={20} /> : <Play size={20} />}</button>
          <button type="button" onClick={next} disabled={!verse} className="press flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl bg-accent text-[17px] font-semibold text-accent-ink disabled:opacity-40">
            Next āyah <ChevronRight size={20} />
          </button>
        </div>
        <div className="mx-auto mt-2 flex max-w-[680px] justify-center">
          <button type="button" onClick={() => update({ showTranslation: !showTranslation })} className="text-[13px] font-semibold text-muted">{showTranslation ? 'Hide' : 'Show'} translation</button>
        </div>
      </footer>

      <Sheet open={celebrate != null} onClose={() => setCelebrate(null)} title="Khatmah complete">
        <div className="space-y-3 px-6 pb-6 text-center">
          <StarIcon size={56} className="mx-auto text-gold" />
          <div className="font-kufi text-[26px] text-gold" dir="rtl">الحمد لله</div>
          <p className="text-[16px]">You completed <b>{k.title}</b>{celebrate && celebrate > 1 ? ` for the ${celebrate}${celebrate === 2 ? 'nd' : celebrate === 3 ? 'rd' : 'th'} time` : ''}.</p>
          <p className="text-[14px] text-muted">{k.cycle ? 'Your next round has started from the beginning.' : 'Begin again whenever you like.'} May Allah accept it from you.</p>
          <button type="button" onClick={() => { setCelebrate(null); nav('/khatmah'); }} className="press w-full rounded-2xl bg-accent py-3 font-semibold text-accent-ink">Back to plans</button>
        </div>
      </Sheet>
      <Sheet open={info} onClose={() => setInfo(false)} title="Ḥasanāt">
        <div className="space-y-3 px-6 pb-6 text-[15px] leading-relaxed">
          <p>The Prophet ﷺ said: <i>“Whoever reads a letter from the Book of Allah receives a good deed, and a good deed is multiplied by ten. I do not say that Alif-Lām-Mīm is a letter; rather Alif is a letter, Lām is a letter and Mīm is a letter.”</i> — at-Tirmidhī 2910 (ḥasan ṣaḥīḥ).</p>
          <p className="text-muted">We count the written letters of each āyah (without vowel marks) × 10. It is only an estimate to encourage you — the reward is with Allah, who multiplies for whom He wills.</p>
          <p className="text-muted">Swipe right or tap the āyah for the next one; swipe left or press Back to go back one āyah (it's taken off today's count, so re-reading it only counts once). Once today's target is reached, Back becomes Khatmah done — swipe left still goes back.</p>
        </div>
      </Sheet>
    </div>
  );
}
